import React from "react";
import { makeStyles } from "@material-ui/core/styles";
import Box from "@material-ui/core/Box";
import Button from "@material-ui/core/Button";
import Checkbox from "@material-ui/core/Checkbox";
import CircularProgress from "@material-ui/core/CircularProgress";
import Drawer from "@material-ui/core/Drawer";
import List from "@material-ui/core/List";
import ListItem from "@material-ui/core/ListItem";
import ListItemIcon from "@material-ui/core/ListItemIcon";
import ListItemText from "@material-ui/core/ListItemText";
import IconButton from "@material-ui/core/IconButton";
import Typography from "@material-ui/core/Typography";
import Close from "@material-ui/icons/Close";
import { useRecoilValue, useSetRecoilState } from "recoil";
import {
  priceRulesState,
  marketGroupState,
  fixtureState,
} from "../../../Atoms/priceRulesAtom";
import { useCreateDisabledStatRuleForMarket } from "../../../hooks/statRulesGroup/createDisabledStatRuleForMarket";
import { useDeleteDisabledStatRuleForMarket } from "../../../hooks/statRulesGroup/deleteDisabledStatRuleForMarket";
import { usePricesForMarkets } from "../../../hooks/statRulesGroup/usePricesForMarkets";

const PriceCheck = (props) => {
  const classes = useStyles();
  const { drawerOpen, setDrawerOpen } = props;
  const createDisabledStatRuleForMarket = useCreateDisabledStatRuleForMarket();
  const deleteDisabledStatRuleForMarket = useDeleteDisabledStatRuleForMarket();

  const priceRules = useRecoilValue(priceRulesState);
  const setPriceRulesState = useSetRecoilState(priceRulesState);
  const markets = useRecoilValue(marketGroupState);
  const fixture = useRecoilValue(fixtureState);
  const { mutate: pricesForMarkets, isLoading: isLoadingPricesForMarkets } =
    usePricesForMarkets();

  // const setPriceRulesState = useSetRecoilState(priceRulesState);
  const marketName = markets?.markets[0].name.split(" | ")[0];

  // Filter out the enabled markets in the models
  const enabledMarketsInModels = markets.models.flatMap((model) =>
    model.statMarkets.filter((market) => market.status === "enabled"),
  );

  // Disable a rule
  const disableRule = (ruleId) => {
    // Get the marketIds from the markets array
    // For the given rule, get the statRuleId and marketIds
    const statRuleId = priceRules.statRulesGroupsApplied
      .filter((group) => group.context.ruleId === ruleId)
      .map((group) => group.statRules.map((rule) => rule.statId))
      .flat();

    // Get the market ids from the markets array if stat is found in the statRuleId
    const marketId = enabledMarketsInModels
      .filter((market) => statRuleId.includes(market.stat))
      .map((market) => market.id);

    const item = {
      fixtureId: fixture.fixtureId,
      rules: marketId.map((id) => {
        return {
          MarketId: id,
          StatRuleId: ruleId,
        };
      }),
    };

    createDisabledStatRuleForMarket.mutate(item, {
      onSuccess: () => {
        setPriceRulesState((oldState) => {
          return {
            ...oldState,
            statRulesGroupsDisabled: [
              ...oldState.statRulesGroupsDisabled,
              priceRules.statRulesGroupsApplied.find(
                (group) => group.context.ruleId === ruleId,
              ),
            ],
          };
        });
      },
    });
  };

  // Enable a rule
  const enableRule = (ruleId) => {
    // Get the marketIds from the markets array
    // For the given rule, get the statRuleId and marketIds
    const statRuleId = priceRules.statRulesGroupsDisabled
      .filter((group) => group.context.ruleId === ruleId)
      .map((group) => group.statRules.map((rule) => rule.statId))
      .flat();

    // Get the market ids from the markets array if stat is found in the statRuleId
    const marketId = enabledMarketsInModels
      .filter((market) => statRuleId.includes(market.stat))
      .map((market) => market.id);

    const item = {
      fixtureId: fixture.fixtureId,
      rules: marketId.map((id) => {
        return {
          MarketId: id,
          StatRuleId: ruleId,
        };
      }),
    };

    deleteDisabledStatRuleForMarket.mutate(item, {
      onSuccess: () => {
        setPriceRulesState((oldState) => {
          return {
            ...oldState,
            statRulesGroupsDisabled: oldState.statRulesGroupsDisabled.filter(
              (group) => group.context.ruleId !== ruleId,
            ),
          };
        });
      },
    });
  };

  // Handle the checkbox change
  const handleChange = (ruleId, isDisabled) => {
    if (isDisabled) {
      enableRule(ruleId);
    } else {
      disableRule(ruleId);
    }
  };

  // Create some local state to store the selected button prices
  const [slip, setSlip] = React.useState([]);

  // Add the selected market to the slip
  const handleAddToSlip = (marketId, price, outcome) => {
    setSlip((oldSlip) => {
      const existingIndex = oldSlip.findIndex(
        (item) => item.marketId === marketId,
      );
      if (existingIndex === -1) {
        // If marketId does not exist, add the object
        return [...oldSlip, { marketId, price, outcome }];
      } else {
        if (oldSlip[existingIndex].outcome === outcome) {
          // If marketId and outcome are found, remove the object
          return oldSlip.filter((item, index) => index !== existingIndex);
        } else {
          // If marketId matches but not outcome, replace outcome and price
          const updatedSlip = [...oldSlip];
          updatedSlip[existingIndex] = {
            ...updatedSlip[existingIndex],
            price,
            outcome,
          };
          return updatedSlip;
        }
      }
    });
  };

  // Local state to store the factored price
  const [factoredPrice, setFactoredPrice] = React.useState(1);

  // Call fetchFactoredPrice whenever slip changes
  React.useEffect(() => {
    if (slip.length > 0) {
      pricesForMarkets(
        { MarketCombos: [...slip] },
        {
          onSuccess: (data) => {
            setFactoredPrice(data.Price);
          },
        },
      );
    }
    // If slip is empty, set the factored price to 1
    if (slip.length === 0) {
      setFactoredPrice(1);
    }
  }, [pricesForMarkets, slip, priceRules]);

  // handleSelectedState for the buttons
  const isSelected = (marketId, outcome) => {
    return slip.some(
      (item) => item.marketId === marketId && item.outcome === outcome,
    );
  };

  // Multiply the prices in the slip
  const totalPrice = slip.reduce((acc, item) => {
    return acc * item.price;
  }, 1);

  return (
    <Drawer
      className={classes.drawer}
      variant="persistent"
      open={drawerOpen}
      anchor="left"
      classes={{
        paper: classes.drawerPaper,
      }}
    >
      <div className={classes.drawerHeader}>
        <Box flex="1">
          <Typography variant="subtitle1">{marketName}</Typography>
        </Box>
        <IconButton
          aria-label="close"
          color="inherit"
          onClick={() => setDrawerOpen("closed")}
        >
          <Close />
        </IconButton>
      </div>
      <div className={classes.drawerContent}>
        <List>
          {priceRules.statRulesGroupsApplied.map((group) => {
            // Check if this rule is disabled
            const isDisabled = priceRules.statRulesGroupsDisabled.some(
              (disabledGroup) =>
                disabledGroup.context.ruleId === group.context.ruleId,
            );

            const statRules = group.statRules
              .map((rule) => rule.statId)
              .join(", ");

            // check if the rule is in conflict
            const isInConflict = priceRules.statRulesGroupsConflicts.some(
              (conflictGroup) =>
                conflictGroup.context.ruleId === group.context.ruleId,
            );

            return (
              <ListItem key={group.context.ruleId}>
                <ListItemIcon>
                  <Checkbox
                    edge="start"
                    checked={!isDisabled} // Invert the checked state for disabled rules
                    tabIndex={-1}
                    disableRipple
                    inputProps={{ "aria-labelledby": group.context.ruleName }}
                    onChange={() =>
                      handleChange(group.context.ruleId, isDisabled)
                    }
                  />
                </ListItemIcon>
                <ListItemText
                  primary={
                    <Box>
                      <Typography component="span">
                        {group.context.ruleName}{" "}
                      </Typography>
                      <Typography component="span">
                        [{group.context.factor}]
                      </Typography>
                      {isInConflict && (
                        <>
                          {" "}
                          <Typography component="span" color="error">
                            (Conflict)
                          </Typography>
                        </>
                      )}
                    </Box>
                  }
                  secondary={statRules}
                />
              </ListItem>
            );
          })}
        </List>
        {enabledMarketsInModels.map((market) => {
          return (
            <Box key={market.id} mb={2}>
              <Box flex="1" textAlign="center">
                {market.stat}
              </Box>
              <Box display="flex">
                <Box flex="1">
                  <Button
                    variant="contained"
                    color={isSelected(market.id, 1) ? "primary" : "default"}
                    fullWidth
                    size="small"
                    onClick={() =>
                      handleAddToSlip(market.id, market.player1_price, 1)
                    }
                  >
                    <Box display="flex" flexDirection="column">
                      <Typography variant="caption">{market.p1Name}</Typography>
                      <div>{market.player1_price}</div>
                    </Box>
                  </Button>
                </Box>
                <Box flex="1" pl={1} pr={1}>
                  <Button
                    variant="contained"
                    color={isSelected(market.id, 0) ? "primary" : "default"}
                    fullWidth
                    size="small"
                    onClick={() =>
                      handleAddToSlip(market.id, market.drawPrice, 0)
                    }
                  >
                    <Box display="flex" flexDirection="column">
                      <Typography variant="caption">Draw</Typography>
                      <div>{market.drawPrice}</div>
                    </Box>
                  </Button>
                </Box>
                <Box flex="1">
                  <Button
                    variant="contained"
                    color={isSelected(market.id, 2) ? "primary" : "default"}
                    fullWidth
                    size="small"
                    onClick={() =>
                      handleAddToSlip(market.id, market.player2_price, 2)
                    }
                  >
                    <Box display="flex" flexDirection="column">
                      <Typography variant="caption">{market.p2Name}</Typography>
                      <div>{market.player2_price}</div>
                    </Box>
                  </Button>
                </Box>
              </Box>
            </Box>
          );
        })}
        <div className={classes.drawerFooter}>
          <Box display="flex" alignItems="center">
            <Box flex="1">
              <Typography variant="subtitle1">Price:</Typography>
            </Box>
            <Typography variant="subtitle1">
              ${totalPrice.toFixed(2)}
            </Typography>
          </Box>
          <Box display="flex" alignItems="center">
            <Box flex="1">
              <Typography variant="subtitle1">Factored: </Typography>
            </Box>
            {isLoadingPricesForMarkets ? (
              <CircularProgress size={16} />
            ) : (
              <Typography variant="subtitle1">
                ${factoredPrice.toFixed(2)}
              </Typography>
            )}
          </Box>
        </div>
      </div>
    </Drawer>
  );
};

export default PriceCheck;

const useStyles = makeStyles((theme) => ({
  drawer: {
    width: "400px",
    flexShrink: 0,
  },
  drawerPaper: {
    width: "400px",
    backgroundColor: theme.palette.background.paper,
    borderRight: `1px solid ${theme.palette.divider}`,
  },
  drawerHeader: {
    backgroundColor: theme.palette.background.default,
    display: "flex",
    alignItems: "center",
    borderBottom: `1px solid ${theme.palette.divider}`,
    padding: theme.spacing(0, 1, 0, 2),
    // necessary for content to be below app bar
    ...theme.mixins.toolbar,
    position: "fixed",
    width: "400px",
    zIndex: "100",
  },
  drawerFooter: {
    position: "fixed",
    bottom: 0,
    left: 0,
    width: "400px",
    padding: theme.spacing(2),
    background: `linear-gradient(0deg, rgba(255,255,255,1) 80%, rgba(255,255,255,0) 100%)`,
  },
  drawerContent: {
    padding: theme.spacing(8, 2, 10, 2),
  },
}));
